import ModalForm from "@ui/Modal/Form";
import React from "react";
import Select from "@ui/Select";
import { styled } from "@linaria/react";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import { useProjects } from "../../../api/useProjects";
import t from "../../../i18n";
import { getProjectRoleName } from "./ProjectRoleMenu";
import { usePost } from "../../../api/useCall";
import { useUser, type GlobalRole, type ProjectRole } from "../../../api/useUser";
import { canUserDoOnProject } from "diagnostics-domain/canUserDo";
import type { UserProfil } from "../../../api/useUsers";
import { comparatorBy } from "diagnostics-lib/comparator";
import { FlaskFill } from "@ui/IconV2/react";
import { color } from "@ui";

const StyledLab = styled(FlaskFill)`
  width: 14px;
  height: 14px;
  margin-left: 4px;
  margin-right: auto;
  color: ${color.carbon800};
`;

interface P {
  userProfil: UserProfil;
  onRequestClose: () => void;
}

function setInitialProjectRole(userGlobalRole: GlobalRole | null): ProjectRole {
  switch (userGlobalRole) {
    case "PROD_READER":
      return "PROJECT_READER";
    case "ALL_PROJECTS_READER":
    case "ALL_PROJECTS_READER_WITH_JUPYTER":
      return "PROJECT_WRITER";
    case "ALL_PROJECTS_WRITER":
    case "ALL_PROJECTS_SETUPER":
      return "PROJECT_SETUPER";
    case "ADMIN":
    case "SUPER_ADMIN":
      return "PROJECT_MANAGER";
    case null:
    case "DIAGNOSER":
      return "PROJECT_READER";
    default:
      throw new Error(userGlobalRole satisfies never);
  }
}

const AddProjectModalForm = ({ userProfil, onRequestClose }: P) => {
  const projects = useProjects();
  const [submitted, setSubmitted] = React.useState(false);
  const selectProject = useInput("");
  const { addToast } = useToasts();
  const updateProjectRoles = usePost("/users/:userId(uuid)/roles/projects/:projectId(uuid)");

  const dirty = submitted || selectProject.dirty;
  const error = !submitted || selectProject.value ? undefined : t("errors.field_cannot_be_empty");
  const valid = !error;
  const user = useUser();

  const handleSubmit = React.useCallback(async () => {
    setSubmitted(true);
    if (!valid) return false;
    const project = projects.find(p => p.name === selectProject.value);
    if (!project) throw new Error("Project not found");
    const userGlobalRole = userProfil.roles.global;
    const projectRole: ProjectRole = setInitialProjectRole(userGlobalRole);
    const messageVariables = {
      userName: userProfil.login,
      projectName: project.name,
      role: getProjectRoleName(projectRole),
    };
    const message = t("users.table.row.roles.project.toast_add_role", messageVariables);

    try {
      await updateProjectRoles({
        routeParams: {
          projectId: project.id,
          userId: userProfil.id,
        },
        body: { role: projectRole },
      });
      addToast("success", message);
    } catch {
      addToast("error", t("errors.backend_error"));
    }
    return true;
  }, [addToast, projects, selectProject.value, userProfil, valid, updateProjectRoles]);

  const options = React.useMemo(() => {
    const projectRoleIds = new Set(userProfil.roles.projects.map(p => p.projectId));

    return projects
      .filter(project => canUserDoOnProject(user, "ASSIGN_PROJECT_ROLE", project.id))
      .map(p => ({
        label: p.name,
        value: p.name,
        icon: p.draft ? <StyledLab /> : null,
        disabled: projectRoleIds.has(p.id),
      }))
      .sort(comparatorBy("label"));
  }, [projects, user, userProfil.roles.projects]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={dirty ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.add")}
    >
      <Select
        required
        filtered
        autoFocus
        label={t("projects.title")}
        prompt={t("users.modal.add_project_to_user.select.select_project")}
        options={options}
        error={error}
        onChange={e => selectProject.onChange(e)}
        // showError={submitted}
        // {...selectProject}
      />
    </ModalForm>
  );
};

export default AddProjectModalForm;
